/**
 * Formatting utilities — shared across all pages
 */

/** Format number in Indian locale (1,23,456) */
export const fmt = (v) =>
  new Intl.NumberFormat('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(v);

/** Format currency in ₹ (Indian Rupees) */
export const fmtCurrency = (v) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(v);

/** Convert number to words in Indian numbering system */
export const numberToWords = (num) => {
  if (num === 0) return 'Zero';
  if (!num || isNaN(num)) return '';
  const a = ['', 'One ', 'Two ', 'Three ', 'Four ', 'Five ', 'Six ', 'Seven ', 'Eight ', 'Nine ', 'Ten ', 'Eleven ', 'Twelve ', 'Thirteen ', 'Fourteen ', 'Fifteen ', 'Sixteen ', 'Seventeen ', 'Eighteen ', 'Nineteen '];
  const b = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  
  const inWords = (n) => {
    n = Math.floor(n);
    let str = '';
    if (n > 9999999) {
      str += inWords(Math.floor(n / 10000000)) + ' Crore ';
      n %= 10000000;
    }
    if (n > 99999) {
      str += inWords(Math.floor(n / 100000)) + ' Lakh ';
      n %= 100000;
    }
    if (n > 999) {
      str += inWords(Math.floor(n / 1000)) + ' Thousand ';
      n %= 1000;
    }
    if (n > 99) {
      str += a[Math.floor(n / 100)] + 'Hundred ';
      n %= 100;
    }
    if (n > 0) {
      if (str !== '') str += 'and ';
      if (n < 20) {
        str += a[n];
      } else {
        str += b[Math.floor(n / 10)] + ' ';
        if (n % 10 > 0) str += a[n % 10];
      }
    }
    return str.trim();
  };
  
  const intPart = Math.floor(num);
  const decPart = Math.round((num - intPart) * 100);
  
  let result = inWords(intPart);
  
  if (result !== '') {
    result += ' Rupees';
  }
  
  if (decPart > 0) {
    if (result === '') {
      result = `${inWords(decPart)} Paise`;
    } else {
      result += ` and ${inWords(decPart)} Paise`;
    }
  }
  
  if (result !== '') {
    result += ' Only';
  }
  
  return result;
};

/** Format date: "22 Apr 2026" */
export const fmtDate = (d) =>
  d
    ? new Date(d).toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
    : '—';

/** Format date + time: "22 Apr, 10:30 AM" */
export const fmtDateTime = (d) =>
  d
    ? new Date(d).toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—';

/** Format relative time: "2 hours ago", "just now" */
export const fmtRelative = (d) => {
  if (!d) return '—';
  const diff = Date.now() - new Date(d).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return fmtDate(d);
};

/** Capitalize first letter */
export const capitalize = (s) =>
  s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : '';

/** Truncate text with ellipsis */
export const truncate = (s, len = 30) =>
  s && s.length > len ? s.slice(0, len) + '…' : s || '';

/** Clamp a number */
export const clamp = (val, min, max) => Math.min(Math.max(val, min), max);

/** Generate initials from a name: "John Doe" → "JD" */
export const getInitials = (name, count = 2) =>
  name
    ? name
        .split(' ')
        .map((w) => w.charAt(0).toUpperCase())
        .slice(0, count)
        .join('')
    : '?';

/** Deep clone a plain object  */
export const deepClone = (obj) => JSON.parse(JSON.stringify(obj));

/** Debounce function */
export const debounce = (fn, delay = 300) => {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delay);
  };
};

/** classNames helper — like clsx */
export const cn = (...classes) => classes.filter(Boolean).join(' ');

// ── Named aliases from theirs (4558b55) — kept for backward compatibility ──

export const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return '-';
  const num = Number(amount);
  if (isNaN(num)) return '-';

  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(num);
};

export const formatDate = (dateString, formatStr) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '-';

  // Standard compact Indian format: DD MMM YYYY
  return date.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

export const formatNumber = (amount) => {
  if (amount === null || amount === undefined) return '-';
  const num = Number(amount);
  if (isNaN(num)) return '-';

  return new Intl.NumberFormat('en-IN').format(num);
};
